/**
 * Menu navigation for keyboard and gamepad: arrows / D-pad / stick move focus spatially between [data-nav]
 * elements, Enter / A activates, Esc / B goes back, Esc / P / Start pauses while riding. Mouse and touch use the
 * buttons directly. The gamepad's riding input is read here too and handed to the game.
 */
import { InputFrame } from '../sim/input';
import { newPadState, PadAction, readPad } from './logic/gamepad';
import { Dir, nextFocus } from './logic/nav';
import type { GameUi } from './ui';

export class UiNavigator {
  private pad = newPadState();
  padInput: InputFrame | null = null;
  padReset = false;
  private repeatDir: Dir | null = null;
  private repeatAt = 0;

  constructor(private readonly ui: GameUi) {
    addEventListener('keydown', (e) => this.onKey(e));
  }

  private items(): HTMLElement[] {
    const scope = this.ui.root.querySelector('.modal') ?? this.ui.root;
    return [...scope.querySelectorAll<HTMLElement>('[data-nav]')].filter((el) => !(el as HTMLButtonElement).disabled && el.offsetParent !== null);
  }

  focusFirst(keep: boolean): void {
    if (this.ui.state.screen === 'play' && !this.ui.state.help) { (document.activeElement as HTMLElement | null)?.blur?.(); return; }
    const items = this.items();
    const active = document.activeElement as HTMLElement | null;
    if (keep && active && items.includes(active)) return;
    const pick = items.find((el) => el.hasAttribute('autofocus')) ?? items.find((el) => !el.classList.contains('help-fab') && !el.classList.contains('icon-btn')) ?? items[0];
    pick?.focus({ preventScroll: true });
  }

  move(dir: Dir): void {
    const items = this.items();
    if (!items.length) return;
    const cur = items.indexOf(document.activeElement as HTMLElement);
    const boxes = items.map((el) => { const r = el.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; });
    const i = cur < 0 ? 0 : nextFocus(boxes, cur, dir);
    if (i >= 0) { items[i]!.focus(); items[i]!.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
  }

  private onKey(e: KeyboardEvent): void {
    const screen = this.ui.state.screen;
    if (screen === 'loading') return;
    if (e.code === 'Escape' || (e.code === 'KeyP' && screen === 'play')) { e.preventDefault(); this.ui.back(); return; }
    if (screen === 'play' && !this.ui.state.help) return; // riding keys belong to the game
    const target = e.target as HTMLElement;
    const isRange = target instanceof HTMLInputElement && target.type === 'range';
    const dir: Dir | null = e.code === 'ArrowUp' ? 'up' : e.code === 'ArrowDown' ? 'down' : e.code === 'ArrowLeft' ? 'left' : e.code === 'ArrowRight' ? 'right' : null;
    if (dir && !(isRange && (dir === 'left' || dir === 'right'))) { e.preventDefault(); this.move(dir); }
    if (e.code === 'Backspace' && !(target instanceof HTMLInputElement)) { e.preventDefault(); this.ui.back(); }
  }

  /** Once per animation frame. */
  poll(now: number): void {
    const pads = navigator.getGamepads?.() ?? [];
    const gp = [...pads].find((p) => p && p.connected);
    if (!gp) { this.padInput = null; return; }
    const standing = this.pad.standing;
    const { input, pressed } = readPad({ axes: gp.axes, buttons: gp.buttons.map((b) => b.value) }, this.pad);
    const riding = this.ui.state.screen === 'play' && !this.ui.state.help && !this.ui.replaying;
    // A is both "accept" and the stance toggle: menu presses must not flip the stance (a rider resumed with A
    // would stand up), and every ride starts seated like the keyboard's.
    if (!riding) this.pad.standing = this.ui.state.screen === 'intro' ? false : standing;
    this.padInput = riding ? input : null;
    if (riding) {
      if (pressed.includes('pause')) this.ui.back();
      if (pressed.includes('reset')) this.padReset = true;
      if (pressed.includes('camera')) this.ui.toggleCamera();
      return;
    }
    this.menuActions(pressed, now);
  }

  private menuActions(pressed: PadAction[], now: number): void {
    for (const a of pressed) {
      if (a === 'accept') (document.activeElement as HTMLElement | null)?.click();
      else if (a === 'back' || a === 'pause') this.ui.back();
      else if (a === 'up' || a === 'down' || a === 'left' || a === 'right') {
        this.moveOrAdjust(a);
        this.repeatDir = a; this.repeatAt = now + 380;
      }
    }
    // Held direction repeats.
    if (this.repeatDir && !this.pad.held.has(this.repeatDir)) this.repeatDir = null;
    if (this.repeatDir && now > this.repeatAt) { this.moveOrAdjust(this.repeatDir); this.repeatAt = now + 140; }
  }

  private moveOrAdjust(d: Dir): void {
    const el = document.activeElement;
    if (el instanceof HTMLInputElement && el.type === 'range' && (d === 'left' || d === 'right')) {
      d === 'left' ? el.stepDown() : el.stepUp();
      el.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    this.move(d);
  }

  /** Beginner mode for the gamepad: LT / B are the one split brake, the foot is automatic. */
  setBeginner(on: boolean): void { this.pad.beginner = on; }

  takePadReset(): boolean { const r = this.padReset; this.padReset = false; return r; }
}
